import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import { selectIsAuth, selectIsDarkMode } from "@shared/slices/common.slice";
import store from "app/store";
import { localStorageService } from "utils/localStorageService";
import { OpenMessageType, UserSource, UtmParams } from "@shared/types";
import {
  getDisplayResultMessageFunction,
  getMarketingWebsiteUrl,
  getNotificationDisplayTime,
  updateLocalStorageUtmData,
} from "@shared/utils/misc";
import message from "antd/es/message";
import type { MessageInstance } from "antd/es/message/interface";
import React, { ReactNode, useEffect, useState } from "react";
import { getSharedOnboardingUserSource } from "@shared/features/onboarding/utils";
import {
  LOCAL_STORAGE_REGISTRATION_DATA_KEY,
  UNSAVED_CHANGES_WARNING_MESSAGE,
} from "@shared/common/constants";
import { BotSourceValueType } from "analytics/events";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils";
import * as Sentry from "@sentry/react";
import { throttle } from "lodash";
import {
  INIT_DATA_QUERY_PARAM,
  IS_WEB_VIEW,
  NEXT_URL_QUERY_PARAM,
} from "common/constants";
import { requestIntercomAction } from "services/webview/webViewBridgeUtils.ts";
import { IntercomActionType } from "@shared/services/webview/types.ts";
import { show, showArticle, showSpace } from "@intercom/messenger-js-sdk";
import { getEnvironmentType, getWebsiteBaseUrl } from "app/shared/utils.ts";
import { Dayjs } from "dayjs";
import { dateToDayjs } from "@shared/utils/date.ts";
import { telegramOpenLink } from "features/telegram/utils.ts";
import i18n from "i18next";

let messageInstance: MessageInstance | null = null;
export const setMessageInstance = (instance: MessageInstance) => {
  messageInstance = instance;
};

const copyTextToClipboardFallback = async (text?: string) => {
  if (!text) return;

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {}

  const el = document.createElement("textarea");
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

export const copyCodeToClipboard = async (
  text: string | Promise<string>
): Promise<void> => {
  let parsedText = "";
  if (text instanceof Promise) {
    try {
      const clipboardItem = new ClipboardItem({
        "text/plain": text.then((data) => {
          if (!data) {
            throw new Error("Empty text");
          }
          return new Blob([data], { type: "text/plain" });
        }),
      });
      await navigator.clipboard.write([clipboardItem]);
      return;
    } catch {}

    parsedText = await text;
  } else {
    parsedText = text;
  }
  return copyTextToClipboardFallback(parsedText);
};

export const openMessage = (
  description: string,
  type?: OpenMessageType,
  duration?: number,
  maxCount?: number
) => {
  const isDarkMode = getIsDarkMode();
  const parsedDescription =
    typeof description === "string"
      ? description.split("\n").map((text, index) => {
          if (index === 0) return <span key={index}>{text}</span>;
          return <div>{text}</div>;
        })
      : description;
  duration =
    duration ||
    (typeof description === "string"
      ? getNotificationDisplayTime(description, true)
      : 8);
  const msg = messageInstance || message;
  msg[type || "success"]({
    content: parsedDescription,
    duration,
    className: isDarkMode ? "dark-mode" : "",
  });
  message.config({ maxCount: maxCount });
};

export const displayResultMessage =
  getDisplayResultMessageFunction(openMessage);

export const confirmWithCallback = (
  confirmModal: any,
  confirmMessage: ReactNode,
  title?: string,
  showIcon = true,
  okText?: string,
  cancelText?: string,
  hideCancel?: boolean
): Promise<boolean> =>
  new Promise((resolve) => {
    confirmModal.confirm({
      title: title || "Confirm",
      icon: showIcon && <ExclamationCircleOutlined />,
      content: confirmMessage,
      okText: okText || "Save",
      cancelText: cancelText || "Cancel",
      cancelButtonProps: hideCancel
        ? { style: { display: "none" } }
        : undefined,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
      centered: true,
    });
  });

export const showUnsavedChangesWarning = (confirmModal: any) =>
  // @ts-ignore
  confirmWithCallback(
    confirmModal,
    UNSAVED_CHANGES_WARNING_MESSAGE,
    "Unsaved changes",
    true,
    "No",
    "Yes"
  );

export const getIsDarkMode = () =>
  !selectIsAuth(store.getState()) && selectIsDarkMode(store.getState());

export const getOnboardingUserSource = () => {
  const registrationData = localStorageService.getValue(
    LOCAL_STORAGE_REGISTRATION_DATA_KEY
  );
  return getSharedOnboardingUserSource(registrationData);
};

export const getAnalyticsBotSource = (): BotSourceValueType | undefined => {
  const userSource = getOnboardingUserSource();
  return userSource === UserSource.DIRECT ? undefined : userSource;
};

export const removeLocalStorageSignalRefKey = () => {
  localStorageService.removeValue(LOCAL_STORAGE_REGISTRATION_DATA_KEY);
};

export const startIntercomChecklist = (checklistId: number) => {
  // @ts-ignore
  window.Intercom?.("startChecklist", checklistId);
};

export const copyImgToClipboard = async (imageUrl?: string) => {
  try {
    if (imageUrl) {
      const copiedImage = await fetch(imageUrl);
      const blobData = await copiedImage.blob();
      const clipboardItemInput = new ClipboardItem({ "image/png": blobData });
      navigator.clipboard
        .write([clipboardItemInput])
        .then(() =>
          openMessage(getCopyMessage(i18n.t("common:copy.image")), "info")
        )
        .catch(() => {});
    }
  } catch (e) {
    Sentry.captureException(
      `Encountered error when running copyImgToClipboard in share image modal: ${e}`
    );
  }
};

export const getDownloadFileUrl = async (fileUrl: string) => {
  const response = await fetch(fileUrl);
  const blobData = await response.blob();
  return window.URL.createObjectURL(new Blob([blobData]));
};

export const openLink = (link: string) => window.open(link, "_blank");

export function useIdle(ms = 1000 * 60) {
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    let timeoutId;

    const handleTimeout = () => {
      setIdle(true);
    };

    const handleEvent = throttle(() => {
      setIdle(false);

      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(handleTimeout, ms);
    }, 500);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleEvent();
      }
    };

    timeoutId = window.setTimeout(handleTimeout, ms);

    window.addEventListener("mousemove", handleEvent);
    window.addEventListener("mousedown", handleEvent);
    window.addEventListener("resize", handleEvent);
    window.addEventListener("keydown", handleEvent);
    window.addEventListener("touchstart", handleEvent);
    window.addEventListener("wheel", handleEvent);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("mousemove", handleEvent);
      window.removeEventListener("mousedown", handleEvent);
      window.removeEventListener("resize", handleEvent);
      window.removeEventListener("keydown", handleEvent);
      window.removeEventListener("touchstart", handleEvent);
      window.removeEventListener("wheel", handleEvent);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearTimeout(timeoutId);
    };
  }, [ms]);

  return idle;
}

export const getWebsiteUrl = (path?: string) => {
  return `${getWebsiteBaseUrl()}${path ?? ""}`;
};

export const redirectWithTelegramWebData = (
  initData: string,
  additionalParams?: Record<string, string>,
  nextPath?: string
): boolean => {
  let parsedInitData = "";
  try {
    parsedInitData = btoa(initData);
  } catch {}
  const query = new URLSearchParams();
  if (parsedInitData) {
    query.set(INIT_DATA_QUERY_PARAM, parsedInitData);
  }
  if (nextPath) {
    query.set(NEXT_URL_QUERY_PARAM, nextPath);
  }
  if (additionalParams) {
    Object.entries(additionalParams).map(([key, value]) =>
      query.set(key, value)
    );
  }
  const websiteUrl = getWebsiteUrl(`?${query.toString()}`);
  return telegramOpenLink(websiteUrl);
};

const marketingWebsiteUrl = getMarketingWebsiteUrl(getEnvironmentType());
const inIframe = window !== window.parent;
if (inIframe) {
  window.addEventListener("message", function (message) {
    let data: any;
    try {
      data = JSON.parse(message.data);
    } catch {}
    if (
      message.origin === marketingWebsiteUrl &&
      !!data &&
      UtmParams.UTM_SOURCE in data
    ) {
      updateLocalStorageUtmData(data);
    }
  });
}

export const getParsedData = (data: string) => {
  try {
    return JSON.parse(data);
  } catch {}
};

export const showIntercomSpace = (space: string) => {
  if (IS_WEB_VIEW) {
    requestIntercomAction({
      type: IntercomActionType.SHOW_SPACE,
      itemId: space,
    }).catch(() => {});
  } else {
    showSpace(space);
  }
};

export const setIntercomLauncherVisibility = (isVisible: boolean) => {
  if (IS_WEB_VIEW) {
    requestIntercomAction({
      type: IntercomActionType.SET_LAUNCHER_VISIBILITY,
      isLauncherVisible: isVisible,
    }).catch(() => {});
  }
};

export const showIntercomArticle = (articleId: string) => {
  if (IS_WEB_VIEW) {
    requestIntercomAction({
      type: IntercomActionType.SHOW_ARTICLE,
      itemId: articleId,
    }).catch(() => {});
  } else {
    showArticle(articleId);
  }
};

export const showIntercom = () => {
  if (IS_WEB_VIEW) {
    requestIntercomAction({
      type: IntercomActionType.SHOW,
    }).catch(() => {});
  } else {
    show();
  }
};

export const getRangePickerValue = (timeframe?: {
  start: number | null;
  end: number | null;
}): [Dayjs | null, Dayjs | null] | undefined => {
  if (!timeframe) return;
  const { start, end } = timeframe;
  if (start || end)
    return [start ? dateToDayjs(start) : null, end ? dateToDayjs(end) : null];
};

export const onTimeframeChange = (
  dates: [Dayjs | null, Dayjs | null] | null,
  setTimeframe: (value?: { start: number | null; end: number | null }) => void
) => {
  if (dates && (dates[0] || dates[1])) {
    setTimeframe({
      start: dates[0]?.startOf("day").unix() ?? null,
      end: dates[1]?.endOf("day").unix() ?? null,
    });
  } else {
    setTimeframe(undefined);
  }
};
